import { usePairMarketScheduleStore } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { PairsV4 } from '@ui/services/contract/hooks/PairsManagerFacet/usePairsV4';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { TradePair } from '@ui/types/tradePair';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useTradePairInfos } from '../../../../hooks/trade/useTradePairInfos';

type PositionListContextValue = {
  feeConfig?: PairsV4['feeConfig'];
  isMarketHourOpen: boolean;
};

const PositionListContext = createContext<PositionListContextValue>({
  isMarketHourOpen: true,
});

type PositionListContextProviderProps = {
  children: ReactNode;
  position: Position;
  tradePair?: TradePair;
  isMarketHourOpen?: boolean;
};

export const PositionListContextProvider = (
  props: PositionListContextProviderProps,
) => {
  const { children, position, tradePair, isMarketHourOpen } = props;

  if (
    Object.prototype.hasOwnProperty.call(props, 'tradePair') ||
    Object.prototype.hasOwnProperty.call(props, 'isMarketHourOpen')
  ) {
    return (
      <PositionListContext.Provider
        value={{
          feeConfig: tradePair?.feeConfig,
          isMarketHourOpen: isMarketHourOpen ?? true,
        }}
      >
        {children}
      </PositionListContext.Provider>
    );
  }

  return (
    <PositionListContextProviderWithHooks position={position}>
      {children}
    </PositionListContextProviderWithHooks>
  );
};

const PositionListContextProviderWithHooks = ({
  children,
  position,
}: {
  children: ReactNode;
  position: Position;
}) => {
  const pairs = useTradePairInfos();
  const chainId = useDataChainId();
  const isOpen = usePairMarketScheduleStore(
    (s) =>
      s.getMarketScheduleByPair(position.pair.pairName, chainId).isClientOpen,
  );
  const context = useMemo(
    () => ({
      feeConfig: pairs?.find(
        (pair) =>
          pair.base.toLowerCase() === position.pair.address.toLowerCase(),
      )?.feeConfig,
      isMarketHourOpen: isOpen,
    }),
    [isOpen, pairs, position.pair.address],
  );

  return (
    <PositionListContext.Provider value={context}>
      {children}
    </PositionListContext.Provider>
  );
};

export const usePositionListContext = () => useContext(PositionListContext);
